const DISPLAY_RULE_PATTERN = /display-(none|only)-([^\s]+)/gi;
const RESPONSIVE_WIDTHS = [70, 140, 165, 355, 450, 535, 710, 900, 1070, 1420];

export function parseDisplayRules(alt = '') {
  const rules = [];
  const source = String(alt);
  let match;

  while ((match = DISPLAY_RULE_PATTERN.exec(source)) !== null) {
    const countries = match[2]
      .split(',')
      .map((country) => country.trim().toUpperCase())
      .filter(Boolean);

    if (countries.length) rules.push({ type: match[1].toLowerCase(), countries });
  }

  DISPLAY_RULE_PATTERN.lastIndex = 0;
  return rules;
}

export function isMediaEligible(media, country = '') {
  const normalizedCountry = String(country).trim().toUpperCase();
  const rules = parseDisplayRules(media?.alt || '');
  let hiddenRule = null;
  let onlyRule = null;

  rules.forEach((rule) => {
    if (rule.type === 'none') hiddenRule = rule;
    if (rule.type === 'only') onlyRule = rule;
  });

  if (hiddenRule?.countries.includes(normalizedCountry)) return false;
  if (onlyRule && !onlyRule.countries.includes(normalizedCountry)) return false;
  return true;
}

export function stripDisplayTokens(alt = '') {
  return String(alt)
    .replace(/(?:^|\s)display-(?:none|only)-[^\s]+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function selectEligibleMedia(media, country = '', limit = 2) {
  return Array.from(media || [])
    .filter((item) => isMediaEligible(item, country))
    .slice(0, limit);
}

export function getEffectiveCountry(cachedCountry, pathname = '') {
  // 🔴 경로가 IP 보다 우선 — PDP(product-media-gallery.liquid 인라인 `경로 OR IP`)와 규칙 통일.
  //    IP 를 먼저 보면 한국 IP 로 /tw/ (언어 변경) 진입 시 SSR 이 그린 TW 이미지를
  //    KR 판정으로 되돌려서 PDP 와 화면이 갈린다 (2026-07-23 실사례).
  if (pathname === '/tw' || pathname.startsWith('/tw/')) return 'TW';
  return String(cachedCountry || '').trim().toUpperCase();
}

export function isCountryResolutionReady(cachedCountry, pathname = '', detectionComplete = false) {
  if (String(cachedCountry || '').trim()) return true;
  if (pathname === '/tw' || pathname.startsWith('/tw/')) return true;
  return detectionComplete === true;
}

export function getRerenderRoots(detail) {
  if (!detail?.appendedCards) return null;
  return Array.from(detail.appendedCards);
}

export function selectHoverAfterMain(eligible, main) {
  const mainIndex = Array.from(eligible || []).indexOf(main);
  if (mainIndex < 0) return null;
  return eligible[mainIndex + 1] || null;
}

export function buildShopifyImageUrl(source, width) {
  if (!source || !width) return source || '';
  const protocolRelative = source.startsWith('//');
  const rootRelative = source.startsWith('/') && !protocolRelative;

  try {
    const url = new URL(source, 'https://shopify.local');
    url.searchParams.set('width', String(width));
    if (protocolRelative) return `//${url.host}${url.pathname}${url.search}`;
    if (rootRelative) return `${url.pathname}${url.search}`;
    return url.toString();
  } catch (_error) {
    return source;
  }
}

export function buildResponsiveSrcset(media, widths = RESPONSIVE_WIDTHS) {
  const maxWidth = Number(media?.width) || 0;
  if (!media?.src || !maxWidth) return '';
  const responsiveWidths = [...new Set([...widths.filter((width) => width < maxWidth), maxWidth])].sort(
    (first, second) => first - second
  );
  return responsiveWidths.map((width) => `${buildShopifyImageUrl(media.src, width)} ${width}w`).join(', ');
}

function normalizeImageUrl(value) {
  if (!value) return '';
  try {
    return new URL(value, window.location.origin).pathname;
  } catch (_error) {
    return String(value).split('?')[0];
  }
}

function readCandidates(card) {
  const template = card.querySelector('template[data-market-media-candidates]');
  if (!template) return [];

  return Array.from(template.content.querySelectorAll('[data-market-media-candidate]')).map((candidate) => ({
    alt: candidate.dataset.marketAlt || '',
    accessibleAlt: candidate.dataset.marketAccessibleAlt || '',
    src: candidate.dataset.marketSrc || '',
    width: Number(candidate.dataset.marketWidth) || 0,
    height: Number(candidate.dataset.marketHeight) || 0,
  }));
}

function copyImage(candidate, target, sizes) {
  if (!candidate?.src || !target) return;
  target.src = buildShopifyImageUrl(candidate.src, candidate.width);
  target.srcset = buildResponsiveSrcset(candidate);
  if (sizes) target.sizes = sizes;
  target.width = candidate.width;
  target.height = candidate.height;
  target.alt = candidate.accessibleAlt || stripDisplayTokens(candidate.alt);
}

function findCurrentCandidate(candidates, image) {
  const currentUrl = normalizeImageUrl(image?.src || image?.currentSrc);
  if (!currentUrl) return null;
  return candidates.find((candidate) => normalizeImageUrl(candidate.src) === currentUrl) || null;
}

function resolveCountry(card) {
  let cachedCountry = window.FoxTheme?.country || '';
  if (!cachedCountry) {
    try {
      cachedCountry = window.sessionStorage.getItem('detectedCountry') || '';
    } catch (_error) {}
  }

  const ready = isCountryResolutionReady(
    cachedCountry,
    window.location.pathname,
    window.FoxTheme?.countryDetectionComplete
  );
  return {
    ready,
    country: ready
      ? getEffectiveCountry(cachedCountry, window.location.pathname) || card.dataset.marketCountry || ''
      : '',
  };
}

function applyCard(card, preserveCurrent = false) {
  const candidates = readCandidates(card);
  const countryResolution = resolveCountry(card);
  if (!countryResolution.ready) return;
  const country = countryResolution.country;
  const eligible = selectEligibleMedia(candidates, country, candidates.length);
  const mainImage = card.querySelector('[data-product-image-main]');
  const secondImage = card.querySelector('[data-product-image-second]');
  const mainWrapper = card.querySelector('[data-market-media-main]');
  const secondWrapper = card.querySelector('[data-market-media-second]');
  const placeholder = card.querySelector('[data-market-media-placeholder]');
  const imageWrapper = mainWrapper?.closest('.product-card__image-wrapper');
  const imageSizes = card.dataset.marketImageSizes || '';

  let main = eligible[0];
  if (preserveCurrent) {
    const current = findCurrentCandidate(candidates, mainImage);
    if (current && eligible.includes(current)) main = current;
  }

  if (!main) {
    if (mainWrapper) mainWrapper.hidden = true;
    if (secondWrapper) secondWrapper.hidden = true;
    if (placeholder) placeholder.hidden = false;
    imageWrapper?.classList.add('product-card__image-wrapper--main-only');
    return;
  }

  copyImage(main, mainImage, imageSizes);
  if (mainWrapper) mainWrapper.hidden = false;
  if (placeholder) placeholder.hidden = true;

  if (!secondImage || !secondWrapper) return;
  const second = selectHoverAfterMain(eligible, main);

  if (second) {
    copyImage(second, secondImage, imageSizes);
    secondWrapper.hidden = false;
    imageWrapper?.classList.remove('product-card__image-wrapper--main-only');
  } else {
    secondWrapper.hidden = true;
    imageWrapper?.classList.add('product-card__image-wrapper--main-only');
  }
}

function applyAll(root = document, preserveCurrent = false) {
  const cards = [];
  if (root.matches?.('[data-market-media-card]')) cards.push(root);
  root.querySelectorAll?.('[data-market-media-card]').forEach((card) => cards.push(card));
  cards.forEach((card) => applyCard(card, preserveCurrent));
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const initialize = () => applyAll(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();

  document.addEventListener('collection:rerendered', (event) => {
    const roots = getRerenderRoots(event.detail);
    if (roots) roots.forEach((root) => applyAll(root));
    else applyAll(document);
  });
  document.addEventListener('recommendations:loaded', (event) => applyAll(event.target || document), true);
  document.addEventListener('country:detected', () => applyAll(document));
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest?.('[data-custom-option-value]');
    const card = swatch?.closest('[data-market-media-card]');
    if (card) setTimeout(() => applyCard(card, true));
  });
}
